/**
 * Settings page — avatar, password change, theme toggle, chat notifications.
 */
import { useMutation } from '@tanstack/react-query';
import { Lock, Sun, Moon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ApiRequestError } from '../../../lib/api.js';
import { useAuthStore } from '../../../store/auth.js';
import { Button } from '../../../shell/Button.js';
import { Input } from '../../../shell/Field.js';
import { toast } from '../../../shell/Toast.js';
import { clsx } from 'clsx';
import { BrandingCard } from '../components/BrandingCard.js';
import { CHAT_ENABLED } from '@shared/features';
import { ChatNotificationSettings } from '../../chat/ChatNotificationSettings.js';
import { AvatarUploadControl } from '../components/AvatarUploadControl.js';

const passwordFormSchema = z.object({
  currentPassword: z.string().min(8),
  newPassword: z.string().min(8),
  confirmPassword: z.string().min(8),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match.',
  path: ['confirmPassword'],
});

type ChangePasswordForm = z.infer<typeof passwordFormSchema>;

export function SettingsPage() {
  const { user, theme, toggleTheme } = useAuthStore();

  const changePassword = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: boolean }>('/api/auth/change-password', body),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ChangePasswordForm>({
    resolver: zodResolver(passwordFormSchema),
  });

  const onPasswordSubmit = async (data: ChangePasswordForm) => {
    try {
      await changePassword.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast('success', 'Password changed successfully.');
      reset();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to change password.';
      toast('error', msg);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Settings</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Manage your account</p>
      </div>

      <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <h2 className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Profile picture</h2>
        <AvatarUploadControl
          name={user.name}
          src={user.pictureUrl}
          variant="settings"
        />
      </section>

      {user.role === 'Admin' && <BrandingCard />}

      {CHAT_ENABLED && <ChatNotificationSettings />}

      <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="mb-4 flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Change password</h2>
        </div>
        <form onSubmit={handleSubmit(onPasswordSubmit)} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            autoComplete="current-password"
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />
          <Input
            label="New Password"
            type="password"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />
          <Input
            label="Confirm New Password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword ? 'Passwords do not match.' : undefined}
            {...register('confirmPassword')}
          />
          <Button type="submit" loading={changePassword.isPending}>Update Password</Button>
        </form>
      </section>

      <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <h2 className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Appearance</h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {theme === 'dark' ? <Moon className="h-4 w-4 text-slate-400" /> : <Sun className="h-4 w-4 text-slate-400" />}
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </span>
          </div>
          <button
            onClick={toggleTheme}
            className={clsx(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              theme === 'dark' ? 'bg-sky-500' : 'bg-slate-300',
            )}
            role="switch"
            aria-checked={theme === 'dark'}
            aria-label="Toggle dark mode"
          >
            <span
              className={clsx(
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                theme === 'dark' ? 'translate-x-6' : 'translate-x-1',
              )}
            />
          </button>
        </div>
      </section>
    </div>
  );
}
